import { useState, useEffect } from 'react';
import { MapPin, ChevronDown, Star, Phone, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BusinessHoursDisplay from '@/components/BusinessHoursDisplay';

const API = `${window.location.origin}/api`;

export default function LocationSelector({ businessId, onLocationChange }) {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(`${API}/businesses/${businessId}/locations`);
        if (response.ok) {
          const data = await response.json();
          const locationsList = Array.isArray(data) ? data : (data.locations || []);
          setLocations(locationsList);
          // Select primary location by default, or first location
          const primary = locationsList.find(l => l.is_primary) || locationsList[0];
          if (primary) {
            setSelectedLocation(primary);
            onLocationChange?.(primary);
          }
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [businessId]);

  const handleSelect = (location) => {
    setSelectedLocation(location);
    setIsOpen(false);
    onLocationChange?.(location);
  };

  if (loading || locations.length === 0) return null;
  if (locations.length === 1) return null; // Don't show selector for single location

  return (
    <div className="relative">
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
        data-testid="location-selector"
      >
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-blue-400" />
          <div className="text-left">
            <p className="text-white font-medium">
              {selectedLocation?.name || 'Select Location'}
            </p>
            <p className="text-zinc-500 text-sm">
              {selectedLocation?.city}, {selectedLocation?.state}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-sm">{locations.length} locations</span>
          <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 z-20 bg-[#0F0F0F] border border-zinc-800 rounded-xl overflow-hidden shadow-xl"
          >
            {locations.map((location) => (
              <button
                key={location.location_id}
                onClick={() => handleSelect(location)}
                className={`w-full p-4 text-left hover:bg-zinc-800/50 transition-colors border-b border-zinc-800 last:border-0 ${
                  selectedLocation?.location_id === location.location_id ? 'bg-zinc-800/30' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-white">{location.name || 'Location'}</p>
                      {location.is_primary && (
                        <span className="px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs">
                          Primary
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-sm">
                      {location.address}
                    </p>
                    <p className="text-zinc-500 text-sm">
                      {location.city}, {location.state} {location.zip_code}
                    </p>
                  </div>
                  
                  {location.review_count > 0 && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-white">{location.average_rating?.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected Location Details */}
      {selectedLocation && (
        <div className="mt-4 space-y-4">
          {/* Phone */}
          {selectedLocation.phone && (
            <div className="flex items-center gap-3 text-zinc-400">
              <Phone className="w-4 h-4" />
              <span>{selectedLocation.phone}</span>
            </div>
          )}
          
          {/* Hours for this location */}
          {selectedLocation.business_hours && (
            <div className="p-4 rounded-xl bg-zinc-900/30 border border-zinc-800">
              <BusinessHoursDisplay hours={selectedLocation.business_hours} compact />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
